// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import { setContinuationToken } from "../pagingHelper.js";
import type { Fleetspace } from "../operationsInterfaces/index.js";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers.js";
import * as Parameters from "../models/parameters.js";
import type { CosmosDBManagementClient } from "../cosmosDBManagementClient.js";
import type { SimplePollerLike, OperationState } from "@azure/core-lro";
import { createHttpPoller } from "@azure/core-lro";
import { createLroSpec } from "../lroImpl.js";
import type {
  FleetspaceResource,
  FleetspaceListNextOptionalParams,
  FleetspaceListOptionalParams,
  FleetspaceListResponse,
  FleetspaceGetOptionalParams,
  FleetspaceGetResponse,
  FleetspaceCreateOptionalParams,
  FleetspaceCreateResponse,
  FleetspaceUpdateOptionalParams,
  FleetspaceUpdateResponse,
  FleetspaceDeleteOptionalParams,
  FleetspaceDeleteResponse,
  FleetspaceListNextResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Class containing Fleetspace operations. */
export class FleetspaceImpl implements Fleetspace {
  private readonly client: CosmosDBManagementClient;

  /**
   * Initialize a new instance of the class Fleetspace class.
   * @param client Reference to the service client
   */
  constructor(client: CosmosDBManagementClient) {
    this.client = client;
  }

  /**
   * Lists all the fleetspaces under a fleet.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param options The options parameters.
   */
  public list(
    resourceGroupName: string,
    fleetName: string,
    options?: FleetspaceListOptionalParams,
  ): PagedAsyncIterableIterator<FleetspaceResource> {
    const iter = this.listPagingAll(resourceGroupName, fleetName, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listPagingPage(resourceGroupName, fleetName, options, settings);
      },
    };
  }

  private async *listPagingPage(
    resourceGroupName: string,
    fleetName: string,
    options?: FleetspaceListOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<FleetspaceResource[]> {
    let result: FleetspaceListResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._list(resourceGroupName, fleetName, options);
      const page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listNext(resourceGroupName, fleetName, continuationToken, options);
      continuationToken = result.nextLink;
      const page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listPagingAll(
    resourceGroupName: string,
    fleetName: string,
    options?: FleetspaceListOptionalParams,
  ): AsyncIterableIterator<FleetspaceResource> {
    for await (const page of this.listPagingPage(resourceGroupName, fleetName, options)) {
      yield* page;
    }
  }

  /**
   * Lists all the fleetspaces under a fleet.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param options The options parameters.
   */
  private _list(
    resourceGroupName: string,
    fleetName: string,
    options?: FleetspaceListOptionalParams,
  ): Promise<FleetspaceListResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, fleetName, options },
      listOperationSpec,
    );
  }

  /**
   * Retrieves the properties of an existing Azure Cosmos DB fleetspace under a fleet
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param fleetspaceName Cosmos DB fleetspace name. Needs to be unique under a fleet.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    fleetName: string,
    fleetspaceName: string,
    options?: FleetspaceGetOptionalParams,
  ): Promise<FleetspaceGetResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, fleetName, fleetspaceName, options },
      getOperationSpec,
    );
  }

  /**
   * Creates an Azure Cosmos DB fleetspace under a fleet.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param fleetspaceName Cosmos DB fleetspace name. Needs to be unique under a fleet.
   * @param body The parameters to provide for the current fleetspace.
   * @param options The options parameters.
   */
  async beginCreate(
    resourceGroupName: string,
    fleetName: string,
    fleetspaceName: string,
    body: FleetspaceResource,
    options?: FleetspaceCreateOptionalParams,
  ): Promise<SimplePollerLike<OperationState<FleetspaceCreateResponse>, FleetspaceCreateResponse>> {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<FleetspaceCreateResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, fleetName, fleetspaceName, body, options },
      spec: createOperationSpec,
    });
    const poller = await createHttpPoller<
      FleetspaceCreateResponse,
      OperationState<FleetspaceCreateResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "azure-async-operation",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Creates an Azure Cosmos DB fleetspace under a fleet.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param fleetspaceName Cosmos DB fleetspace name. Needs to be unique under a fleet.
   * @param body The parameters to provide for the current fleetspace.
   * @param options The options parameters.
   */
  async beginCreateAndWait(
    resourceGroupName: string,
    fleetName: string,
    fleetspaceName: string,
    body: FleetspaceResource,
    options?: FleetspaceCreateOptionalParams,
  ): Promise<FleetspaceCreateResponse> {
    const poller = await this.beginCreate(
      resourceGroupName,
      fleetName,
      fleetspaceName,
      body,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Update the properties of an existing Azure Cosmos DB fleetspace under a fleet.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param fleetspaceName Cosmos DB fleetspace name. Needs to be unique under a fleet.
   * @param options The options parameters.
   */
  async beginUpdate(
    resourceGroupName: string,
    fleetName: string,
    fleetspaceName: string,
    options?: FleetspaceUpdateOptionalParams,
  ): Promise<SimplePollerLike<OperationState<FleetspaceUpdateResponse>, FleetspaceUpdateResponse>> {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<FleetspaceUpdateResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, fleetName, fleetspaceName, options },
      spec: updateOperationSpec,
    });
    const poller = await createHttpPoller<
      FleetspaceUpdateResponse,
      OperationState<FleetspaceUpdateResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "azure-async-operation",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Update the properties of an existing Azure Cosmos DB fleetspace under a fleet.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param fleetspaceName Cosmos DB fleetspace name. Needs to be unique under a fleet.
   * @param options The options parameters.
   */
  async beginUpdateAndWait(
    resourceGroupName: string,
    fleetName: string,
    fleetspaceName: string,
    options?: FleetspaceUpdateOptionalParams,
  ): Promise<FleetspaceUpdateResponse> {
    const poller = await this.beginUpdate(resourceGroupName, fleetName, fleetspaceName, options);
    return poller.pollUntilDone();
  }

  /**
   * Deletes an existing Azure Cosmos DB Fleetspace.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param fleetspaceName Cosmos DB fleetspace name. Needs to be unique under a fleet.
   * @param options The options parameters.
   */
  async beginDelete(
    resourceGroupName: string,
    fleetName: string,
    fleetspaceName: string,
    options?: FleetspaceDeleteOptionalParams,
  ): Promise<SimplePollerLike<OperationState<FleetspaceDeleteResponse>, FleetspaceDeleteResponse>> {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<FleetspaceDeleteResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, fleetName, fleetspaceName, options },
      spec: deleteOperationSpec,
    });
    const poller = await createHttpPoller<
      FleetspaceDeleteResponse,
      OperationState<FleetspaceDeleteResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "azure-async-operation",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Deletes an existing Azure Cosmos DB Fleetspace.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param fleetspaceName Cosmos DB fleetspace name. Needs to be unique under a fleet.
   * @param options The options parameters.
   */
  async beginDeleteAndWait(
    resourceGroupName: string,
    fleetName: string,
    fleetspaceName: string,
    options?: FleetspaceDeleteOptionalParams,
  ): Promise<FleetspaceDeleteResponse> {
    const poller = await this.beginDelete(resourceGroupName, fleetName, fleetspaceName, options);
    return poller.pollUntilDone();
  }

  /**
   * ListNext
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param nextLink The nextLink from the previous successful call to the List method.
   * @param options The options parameters.
   */
  private _listNext(
    resourceGroupName: string,
    fleetName: string,
    nextLink: string,
    options?: FleetspaceListNextOptionalParams,
  ): Promise<FleetspaceListNextResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, fleetName, nextLink, options },
      listNextOperationSpec,
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const listOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.DocumentDB/fleets/{fleetName}/fleetspaces",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.FleetspaceListResult,
    },
    default: {
      bodyMapper: Mappers.ErrorResponse,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.fleetName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.DocumentDB/fleets/{fleetName}/fleetspaces/{fleetspaceName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.FleetspaceResource,
    },
    default: {
      bodyMapper: Mappers.ErrorResponse,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.fleetName,
    Parameters.fleetspaceName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const createOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.DocumentDB/fleets/{fleetName}/fleetspaces/{fleetspaceName}",
  httpMethod: "PUT",
  responses: {
    200: {
      bodyMapper: Mappers.FleetspaceResource,
    },
    201: {
      bodyMapper: Mappers.FleetspaceResource,
    },
    202: {
      bodyMapper: Mappers.FleetspaceResource,
    },
    204: {
      bodyMapper: Mappers.FleetspaceResource,
    },
    default: {
      bodyMapper: Mappers.ErrorResponse,
    },
  },
  requestBody: Parameters.body5,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.fleetName,
    Parameters.fleetspaceName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const updateOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.DocumentDB/fleets/{fleetName}/fleetspaces/{fleetspaceName}",
  httpMethod: "PATCH",
  responses: {
    200: {
      bodyMapper: Mappers.FleetspaceResource,
    },
    201: {
      bodyMapper: Mappers.FleetspaceResource,
    },
    202: {
      bodyMapper: Mappers.FleetspaceResource,
    },
    204: {
      bodyMapper: Mappers.FleetspaceResource,
    },
    default: {
      bodyMapper: Mappers.ErrorResponse,
    },
  },
  requestBody: Parameters.body6,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.fleetName,
    Parameters.fleetspaceName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const deleteOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.DocumentDB/fleets/{fleetName}/fleetspaces/{fleetspaceName}",
  httpMethod: "DELETE",
  responses: {
    200: {
      headersMapper: Mappers.FleetspaceDeleteHeaders,
    },
    201: {
      headersMapper: Mappers.FleetspaceDeleteHeaders,
    },
    202: {
      headersMapper: Mappers.FleetspaceDeleteHeaders,
    },
    204: {
      headersMapper: Mappers.FleetspaceDeleteHeaders,
    },
    default: {
      bodyMapper: Mappers.ErrorResponse,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.fleetName,
    Parameters.fleetspaceName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.FleetspaceListResult,
    },
    default: {
      bodyMapper: Mappers.ErrorResponse,
    },
  },
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.nextLink,
    Parameters.fleetName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
